import { Body, Children, Message, Option, ThirdpartyDocumentsAemItem } from '@app/models/aem.model' ;
import {
  ADD_DUPLICATE_ADDRESS_HEADING_LABEL_GROUP,
  CHANGE_ADDRESS_AND_ITS_ACCOUNT,
  CONFIRMATION_ADD_PAGE_CONTAINER,
  DELETE_DUPLICATE_STATEMENT_ADDRESS_INFO_MESSAGE,
  DELETE_DUPLICATE_STATEMENT_ADDRESS_PAGE_CONTAINER,
  DUPLICATE_STATEMENTS_CONFIRMATIONS_FLOW_FORM_CONTAINER,
  ENROLL_EDELIVERY_CARD,
  ENROLL_IN_EDELIVERY_LINK, ENTER_RECIPIENT_INFO_FORM_CONTAINER,
  FUND_AND_ACCOUNT_LABEL,
  INTERESTED_PARTY_SELECTOR_DROPDOWN,
  NAME_LABEL,
  SYMBOL_LABEL,
  VIEW_STATEMENTS_CARD,
  VIEW_STATEMENTS_LINK,
  VISIT_TAX_CENTER_CARD,
  VISIT_TAX_CENTER_LINK,
} from '@app/constants/aem.constant';
import { environment } from '@environments/environment';

export class AemUtil {
  // @ts-ignore
  static getChildren(childPathContext: string, children: Children[] | null): Children {
    if (children && children.length > 0) {
      // Find the first child that matches the childPathContext
      // and return it. This assumes that childPathContext is a unique identifier for the child.
      // If there are multiple children with the same path, this will return the first one found.
      // If no children match, it will return undefined.
      // @ts-ignore
      return children.find(child => child._path.includes(childPathContext)) || undefined;
    }
  }

  static getFaqOptionsFromPath(childPathContext: string, children: Children[] | null | undefined): Option[] {
    if (!children || children.length === 0) return [];

    const matched = children.find(child => child._path?.includes(childPathContext));
    if (!matched?.children || matched.children.length === 0) return [];

    return matched.children.map(child => ({
      'optionKey': child.heading,
      'optionValue': AemUtil.getHtmlBody(child.body),
    }));
  }

  static getHeadingFromChildren(childPathContext: string, children: Children[] | null | undefined): string | undefined {
    const matched = children?.find(child => child._path?.includes(childPathContext));
    return matched?.heading ?? undefined;
  }

  static getLabelTextFromChildren(childPathContext: string, children: Children[] | null | undefined): string | undefined {
    const matched = children?.find(child => child._path?.includes(childPathContext));
    return matched?.labelText ?? undefined;
  }

  static getBodyContentFromChildren(childPathContext: string, children: Children[] | null | undefined): string | undefined {
    const matched = children?.find(child => child._path?.includes(childPathContext));
    return matched ? AemUtil.getHtmlBody(matched.body) ?? undefined : undefined;
  }

  static getErrorMessagesFromMessages(messages: Message[] | null | undefined, pathFragment: string): string {
    const matched = messages?.find(msg => msg._path?.includes(pathFragment));
    return matched ? AemUtil.getHtmlBody(matched.body) : '';
  }

  static getHeadingFromMessages(messages: Message[] | null | undefined, pathFragment: string): string|undefined|null {
    return messages?.find(msg => msg._path?.includes(pathFragment))?.heading;
  }

  static getTempErrorArrFromMessages(message: Message[] | null | undefined): {
    value: string | undefined;
    key: string
  }[] | undefined {

    if (message && message.length > 0) {
      return message.map((key) => {
        const pathSegments = key._path ? key._path.split('/') : [];
        const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';
        return {

          'key': lastSegment,
          'value': AemUtil.getHtmlTextFromBodyChild(key.body),
        };
      });
    }
    return undefined;
  }

  static getThirdpartDocumentsData(searchText: string[], children: Children[] | null | undefined): {
    labelText: string;
    href: string
  }[] {
    if (!children || children.length === 0) return [];

    return searchText.flatMap(path =>
      children
        .filter(child => child._path && child._path.includes(path))
        .map(child => ({
          'labelText': child.labelText || '',
          'href': child.url || '',
        })),
    );
  }

  static getTableColumnLabels(content: any, tablePathFragment: string): {
    symbol: string;
    name: string;
    fundAccount: string
  } {
    const table = content?.tables?.find((t: any) =>
      t._path?.includes(tablePathFragment),
    );
    const getLabel = (fragment: string) =>
      table?.columnLabels?.find((label: any) => label._path?.includes(fragment))?.labelText ?? '';
    return {
      symbol: getLabel(SYMBOL_LABEL),
      name: getLabel(NAME_LABEL),
      fundAccount: getLabel(FUND_AND_ACCOUNT_LABEL),
    };
  }

  static getLabelFromTables(content: ThirdpartyDocumentsAemItem | null, labelPathFragment: string): string {
    return content?.tables?.[0]?.columnLabels?.find(
      label => label._path?.includes(labelPathFragment),
    )?.labelText ?? labelPathFragment;
  }

  static getLabelTextFromLabels(content: ThirdpartyDocumentsAemItem | null, labelPathFragment: string): string {
    return content?.labels?.find(
      label => label._path?.includes(labelPathFragment),
    )?.labelText ?? labelPathFragment;
  }

  static getStepLabelsFromAem(content: ThirdpartyDocumentsAemItem | null): string[] {
    const pathFragment = ADD_DUPLICATE_ADDRESS_HEADING_LABEL_GROUP;
    const matchedGroup = content?.labels?.find(label => label._path?.includes(pathFragment));
    const children = matchedGroup?.children ?? [];
    // @ts-ignore
    return children.map((child: Children) => child.labelText).filter(label => !!label);
  }

  static getFormFieldsFromNestedForm(content: any, containerPathFragment: string,
  ): {
    labelText: string;
    placeholder: string | null;
    hintText: string | null;
    primaryErrorMessage: string | null;
    secondaryErrorMessage: string | null;
  }[] {
    const formContainer = content?.form?.find((f: any) =>
      f._path?.includes(DUPLICATE_STATEMENTS_CONFIRMATIONS_FLOW_FORM_CONTAINER),
    );

    return formContainer?.children?.flatMap((child: any) => {
      if (child._path?.includes(containerPathFragment)) {
        return (child.formFields || []).map((field: any) => ({
          labelText: field.labelText || '',
          placeholder: field.placeholder || null,
          hintText: field.hintText || null,
          primaryErrorMessage: field.primaryErrorMessage?.labelText || null,
          secondaryErrorMessage: field.secondaryErrorMessage?.labelText || null,
        }));
      }
      return [];
    }) || [];
  }

  static getBrokerageFormDetails(
    content: any,
    containerPathFragment: string,
  ): {
    containerHeading: string;
    fieldHeading: string;
    fields: {
      labelText: string;
      placeholder: string | null;
      altText: string | null;
      primaryErrorMessage: string | null;
      secondaryErrorMessage: string | null;
    }[];
  } | null {
    const formContainer = content?.form?.find((f: any) =>
      f._path?.includes(DUPLICATE_STATEMENTS_CONFIRMATIONS_FLOW_FORM_CONTAINER),
    );

    const targetChild = formContainer?.children?.find((child: any) =>
      child._path?.includes(containerPathFragment),
    );

    if (!targetChild) return null;

    const field = targetChild.formFields?.[0];
    const fieldHeading = field?.heading || '';
    const containerHeading = targetChild.heading || '';

    const nestedFields = (field?.children || []).map((nested: any) => ({
      labelText: nested.labelText || '',
      altText: nested.altText || '',
      placeholder: nested.placeholder || null,
      primaryErrorMessage: null,
      secondaryErrorMessage: null,
    }));

    const directField = {
      labelText: field?.labelText || fieldHeading,
      placeholder: field?.placeholder || null,
      primaryErrorMessage: field?.primaryErrorMessage?.labelText || null,
      secondaryErrorMessage: field?.secondaryErrorMessage?.labelText || null,
    };

    return {
      containerHeading,
      fieldHeading,
      fields: [directField, ...nestedFields],
    };
  }

  static getPrimaryCtaLabel(content: ThirdpartyDocumentsAemItem | null, pathFragment: string): string {
    return content?.primaryCta?.children?.find(
      child => child._path?.includes(pathFragment),
    )?.labelText ?? pathFragment;
  }

  static getSecondaryCtaLabel(content: ThirdpartyDocumentsAemItem | null, pathFragment: string): string {
    return content?.secondaryCta?.children?.find(
      child => child._path?.includes(pathFragment),
    )?.labelText ?? pathFragment;
  }

  static getConfirmationPageDetails(content: any): {
    heading: string;
    additionalHeading: string;
    bodyHtml: string;
  } {
    const container = content?.children?.find((c: any) =>
      c._path?.includes(CONFIRMATION_ADD_PAGE_CONTAINER),
    );

    return {
      heading: container?.heading ?? '',
      additionalHeading: container?.additionalHeadings?.[0] ?? '',
      bodyHtml: container?.body?.[0]?.html ?? '',
    };
  }

  static getPageContainerHeading(content: ThirdpartyDocumentsAemItem | null, pathFragment: string): string {
    return content?.children?.find(
      child => child._path?.includes(pathFragment),
    )?.heading ?? pathFragment;
  }

  static getSelectedAccountLable(content: ThirdpartyDocumentsAemItem | null, pathFragment: string): string {
    return content?.children?.find(
      child => child._path?.includes(pathFragment),
    )?.subheading ?? pathFragment;
  }

  static buildActionMenuDataFromAem(secondaryCtaChildren: Children[] | null | undefined): any {
    if (!Array.isArray(secondaryCtaChildren)) return { groups: [] };

    const dropdown = secondaryCtaChildren.find(child =>
      child._path?.includes(INTERESTED_PARTY_SELECTOR_DROPDOWN),
    );

    const dropdownItems = dropdown?.children ?? [];

    const mappedItems = dropdownItems.map((item: any) => {
      const label = item.labelText ?? '';
      const routerLink = label === CHANGE_ADDRESS_AND_ITS_ACCOUNT ? 'add-address-home' : undefined;
      const href = routerLink ? '' : item.url ?? '';

      return { labelText: label, routerLink, href };
    });

    return {
      groups: [
        {
          items: mappedItems,
        },
      ],
    };
  }

  static getPageMessageHeading(content: Message[] | null, pathFragment: string): string {
    if (!content || content.length === 0) return pathFragment;
    const matchedMessage = content.find(
      message => message._path && message._path.includes(pathFragment),
    );
    return matchedMessage?.heading?.trim() || pathFragment;
  }

  static getDeleteAddressMessageHeading(content: any): string {
    const container = content?.find((c: any) =>
      c._path?.includes(DELETE_DUPLICATE_STATEMENT_ADDRESS_PAGE_CONTAINER),
    );

    const message = container?.messages?.find((m: any) =>
      m._path?.includes(DELETE_DUPLICATE_STATEMENT_ADDRESS_INFO_MESSAGE),
    );

    return message?.heading ?? '';
  }

  static getInfoMessage(content: ThirdpartyDocumentsAemItem | null, pathFragment: string): string {
    const child = content?.children?.find(c => c._path?.includes(pathFragment));
    return child?.body ? AemUtil.getHtmlBody(child.body) : pathFragment;
  }

  static getHtmlTextFromBodyChild(body: any): string {
    const extractText = (html: string | null) =>
      html ? html.replace(/<[^>]*>/g, '').trim() : '';
    if (Array.isArray(body)) {
      return body.map(b => extractText(b.html)).join(' ').trim();
    } else if (typeof body === 'object') {
      return extractText(body.html);
    }
    return '';
  }

  static getHtmlBody(body: any): string {
    if (Array.isArray(body)) {
      return body.map(b => b.html).join(' ').trim();
    } else if (typeof body === 'object') {
      return body.html?.trim() ?? '';
    }
    return '';
  }

  static getFormFieldLabel(content: any, fieldPathFragment: string): string {
    const formChildren = content?.form?.[0]?.children || [];

    for (const child of formChildren) {
      const field = child.formFields?.find((f: any) => f._path?.includes(fieldPathFragment));
      if (field) {
        return field.labelText || fieldPathFragment;
      }
    }

    return fieldPathFragment;
  }

  static getFormAddAddress(content: any): { heading: string; additionalHeadings: string; html: string } | null {
    const formContainer = content?.form?.find((f: any) =>
      f._path?.includes(DUPLICATE_STATEMENTS_CONFIRMATIONS_FLOW_FORM_CONTAINER),
    );

    const addAddressChild = formContainer?.children?.find((child: any) =>
      child._path?.includes(ENTER_RECIPIENT_INFO_FORM_CONTAINER),
    );

    if (!addAddressChild) return null;

    return {
      heading: addAddressChild.heading || '',
      additionalHeadings: addAddressChild.additionalHeadings?.[0] || '',
      html: this.getHtmlBody(addAddressChild.body),
    };
  }

  static getOptimizeTiles(children: Children[] | null, additionalCta: any): any[] {
    if (!Array.isArray(children) || !additionalCta?.children) {
      return [];
    }

    // Map each card/link to its environment key
    const cardIdentifiers = [
      { cardPath: ENROLL_EDELIVERY_CARD, linkLabel: ENROLL_IN_EDELIVERY_LINK, envKey: 'ENROLL_IN_E_DELIVERY' },
      { cardPath: VIEW_STATEMENTS_CARD, linkLabel: VIEW_STATEMENTS_LINK, envKey: 'VIEW_STATEMENTS' },
      { cardPath: VISIT_TAX_CENTER_CARD, linkLabel: VISIT_TAX_CENTER_LINK, envKey: 'VISIT_TAX_CENTER' },
    ];

    return cardIdentifiers.map(({ cardPath, linkLabel, envKey }) => {
      const card = children.find(child => child._path && child._path.includes(cardPath));
      const link = additionalCta.children.find((cta: any) => cta._path && cta._path.includes(linkLabel));

      return {
        tileTitle: link?.labelText || '',
        tileText: card ? AemUtil.getHtmlBody(card.body) : '',
        linkLabel: link?.labelText || '',
        linkPath: environment[envKey]?environment[envKey]:(link?.url ?? ''),
      };
    });
  }
}
